// Import tooltip functions from separate files
import { keyDriversTooltip } from "./chartTooltips/keyDriversTooltip";
import { workflowTooltip } from "./chartTooltips/workflowTooltip";
import { csatTooltip } from "./chartTooltips/csatTooltip";
import {
  dataRowHtml,
  valueDifferenceHtml,
  percentChangeHtml,
} from "./chartTooltips/shared";

export const chartTooltipHtml = {
  keyDrivers: keyDriversTooltip,
  keyDriversWorkflow: workflowTooltip,
  keyDriversCsat: csatTooltip,
  default(args) {
    const { heading, d, cd, rows, hidePercentChange } = args;

    return `
      <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
        <div class='flex border-b pb-1.5 mb-1'>
          <div class='grow font-medium'>
            ${window.scrubHtml(heading)}
          </div>
          ${
            cd && !hidePercentChange
              ? `
            <div class='ml-12'>
              ${percentChangeHtml(d.value, cd.value)}
            </div>
          `
              : ""
          }
        </div>
        ${!!rows && rows.length > 0 ? rows.map((r) => dataRowHtml(window.scrubHtml(r.label), r.formattedValue, false, r.color)).join("") : ""}
        ${!rows ? dataRowHtml(window.scrubHtml(d.label), d.formattedValue) : ""}
        ${!rows && cd ? dataRowHtml(window.scrubHtml(cd.label), cd.formattedValue, true) : ""}
      </div>
    `;
  },
  workflowAverage(args) {
    const { heading, d, cd, rows, hidePercentChange, lowerIsBetter } = args;

    return `
      <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
        <div class='flex border-b pb-1.5 mb-1'>
          <div class='grow font-medium'>
            ${heading}
          </div>
          ${
            cd && !hidePercentChange
              ? `
            <div class='ml-12'>
              ${valueDifferenceHtml(d.value, cd.value, lowerIsBetter)}
            </div>
          `
              : ""
          }
        </div>
        ${!!rows && rows.length > 0 ? rows.map((r) => dataRowHtml(r.label, r.formattedValue, false, r.color)).join("") : ""}
        ${!rows ? dataRowHtml(d.label, d.formattedValue) : ""}
        ${!rows && cd ? dataRowHtml(cd.label, cd.formattedValue, true) : ""}
      </div>
    `;
  },
  scatterplot(args) {
    const { xMeasurementLabel, yMeasurementLabel } = args;

    return function (d, axis) {
      return `
        <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
          <div class='flex border-b pb-1.5 mb-1'>
            <div class='grow font-medium'>
              ${d.squad_name}
            </div>
          </div>
          <div class='flex items-center text-gray-700 text-tiny pt-1.5'>
            <div class='h-2 w-2 rounded-full bg-gray-300'>
            </div>
            <div class='ml-1.5 grow'>
              ${axis.y.tooltipLabel}
            </div>
            <div class='ml-12'>
              ${d.y}${yMeasurementLabel}
            </div>
          </div>
          <div class='flex items-center text-gray-700 text-tiny pt-1.5'>
            <div class='h-2 w-2 rounded-full bg-gray-300'>
            </div>
            <div class='ml-1.5 grow'>
              ${axis.x.tooltipLabel}
            </div>
            <div class='ml-12'>
              ${d.x}${xMeasurementLabel}
            </div>
          </div>
        </div>
      `;
    };
  },
  timeLoss(args) {
    const { heading, d, cd } = args;

    return `
      <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
        <div class='flex border-b pb-1.5 mb-1'>
          <div class='grow font-medium'>
            ${heading}
          </div>
          ${
            cd
              ? `
            <div class='ml-12'>
            ${percentChangeHtml(d.value, cd.value, true)}
            </div>
          `
              : ""
          }
        </div>
        ${dataRowHtml(d.label, d.formattedValue)}
        ${cd ? dataRowHtml(cd.label, cd.formattedValue, true) : ""}
      </div>
    `;
  },
  onboarding(d) {
    return `
      <div class='bg-white rounded-md shadow border text-sm w-56 px-2.5 py-2'>
        <div class='font-medium'>
          ${window.scrubHtml(d.name)}
        </div>
        <div class='text-2xs text-gray-500'>
          Started ${d.start_date}
        </div>
        <div class='mt-3.5'>
          <div class='text-2xs text-gray-500'>
            ${d.label}
          </div>
          <div class='text-tiny'>
            ${d.value_display}
          </div>
        <div>
        ${
          d.squad_name
            ? `
          <div class='mt-3.5'>
            <div class='text-2xs text-gray-500'>
              Team
            </div>
            <div class='text-tiny'>
              ${window.scrubHtml(d.squad_name)}
            </div>
          </div>
        `
            : ""
        }
      </div>
    `;
  },
  attritionRisk(args) {
    const { heading, d } = args;

    return `
      <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
        <div class='flex border-b pb-1.5 mb-1'>
          <div class='grow font-medium'>
            ${heading}
          </div>
        </div>
        ${dataRowHtml(d.label, d.formattedValue)}
        ${dataRowHtml("Contributors at risk", d.contributorCount)}
      </div>
    `;
  },
  participationTrend(args) {
    const { heading, d, cd, color } = args;

    return `
      <div class='bg-white rounded-md shadow border text-tiny px-2 py-1.5'>
        <div class='flex border-b pb-1.5 mb-1'>
          <div class='grow font-medium'>
            ${heading}
          </div>
        </div>
        ${dataRowHtml(d.label, d.formattedValue, false, color)}
        ${cd ? dataRowHtml(cd.label, cd.formattedValue, true) : ""}
      </div>
    `;
  },
  priorityDiffTooltip(item, responseCount) {
    let percentage =
      !!responseCount && responseCount > 0
        ? ` (${Math.round(((item.priority * 1.0) / responseCount) * 100)}%)`
        : "";

    return `${item.priority}${responseCount > 0 ? ` out of ${responseCount}` : ""} people ${percentage} voted for <span class='font-semibold'>${window.scrubHtml(item.name)}</span> as an area they would like improved.`;
  },
};
